import React, { useState, useMemo, useEffect } from 'react';
import {
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  TvIcon,
  FingerPrintIcon,
  CpuChipIcon,
  GlobeAltIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ServerStackIcon,
  CodeBracketIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  IdentificationIcon,
  WifiIcon,
  Battery50Icon,
  BoltIcon,
  WindowIcon,
  LanguageIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  Squares2X2Icon,
  MapPinIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import useTranslation from '../services/translationService';
import { resolveDeviceModel } from '../utils/deviceModels';
import DeviceMockup from './DeviceMockup';

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

const ACRONYMS = new Set([
  'ua', 'dpr', 'id', 'ip', 'os', 'url', 'tls', 'asn', 'http', 'rtt', 'gpu',
  'webgl', 'webgpu', 'webrtc', 'js', 'hls', 'h264', 'vp9', 'iso', 'usb', 'pdf',
  'gb', 'mb', 'ms', 'sec', 'api', 'dnt', 'wow64', 'utc', 'cpu', 'ios', 'x'
]);

/** "userAgentParsed" -> "User Agent Parsed", "viewer-country-name" -> "Viewer Country Name" */
export const humanizeKey = (key) => {
  if (key === null || key === undefined) return '';
  const str = String(key);
  if (str.startsWith('_')) return str.replace(/^_+/, '').toUpperCase();
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_.]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

/** DynamoDB string sets can arrive as Array, Set, {values:[]}, or a plain object. */
export const normalizeStringSet = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value instanceof Set) return Array.from(value);
  if (typeof value === 'string') return [value];
  if (typeof value === 'object') {
    if (Array.isArray(value.values)) return value.values.filter(Boolean);
    return Object.values(value).filter((v) => typeof v === 'string');
  }
  return [];
};

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  v === '' ||
  v === 'unknown' ||
  (Array.isArray(v) && v.length === 0) ||
  (isPlainObject(v) && Object.keys(v).length === 0);

/** Recursively drop null / empty values so the UI never shows "n/a" rows. */
export const pruneEmpty = (value) => {
  if (Array.isArray(value)) {
    const arr = value.map(pruneEmpty).filter((v) => !isEmptyValue(v));
    return arr;
  }
  if (isPlainObject(value)) {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const pv = pruneEmpty(v);
      if (!isEmptyValue(pv)) out[k] = pv;
    });
    return out;
  }
  return value;
};

const countLeaves = (value) => {
  if (Array.isArray(value)) return value.reduce((n, v) => n + countLeaves(v), 0);
  if (isPlainObject(value)) return Object.values(value).reduce((n, v) => n + countLeaves(v), 0);
  return isEmptyValue(value) ? 0 : 1;
};

const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(typeof value === 'number' && value < 1e12 ? value * 1000 : value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return String(bytes);
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let i = -1;
  let v = n;
  do {
    v /= 1024;
    i += 1;
  } while (v >= 1024 && i < units.length - 1);
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

const looksLikeTimestampKey = (key) =>
  /(At|Time|Origin)$/.test(key) || /^(capturedAt|localTime|isoTime|requestTimeEpoch|idCreatedAt)$/.test(key);

const looksLikeBytesKey = (key) => /Bytes$|HeapSize/i.test(key);

const looksLikeMonoKey = (key) =>
  /(id|Id|ID|hash|Hash|fingerprint|Fingerprint|userAgent|UserAgent|ua|renderer|Renderer|token|trace|address|Address|href|origin|referer|referrer|ip|Ip|IP)$/.test(key) ||
  /^(ua|x-|sec-|cloudfront-)/.test(key);

const relativeTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  let label;
  if (abs < minute) return 'just now';
  if (abs < hour) label = `${Math.floor(abs / minute)} min`;
  else if (abs < day) label = `${Math.floor(abs / hour)} h`;
  else if (abs < 30 * day) label = `${Math.floor(abs / day)} d`;
  else label = `${Math.floor(abs / (30 * day))} mo`;
  return diff >= 0 ? `${label} ago` : `in ${label}`;
};

const shortId = (value, head = 8, tail = 4) => {
  if (!value) return '';
  const s = String(value);
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const safeDisplayName = (type, code) => {
  if (!code) return null;
  try {
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      const dn = new Intl.DisplayNames(undefined, { type });
      const out = dn.of(code);
      return out && out !== code ? out : null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

/** "ar-LB" -> "Arabic (Lebanon)" */
const describeLanguage = (tag) => {
  if (!tag) return null;
  const clean = String(tag).split(';')[0].trim();
  const [lang, region] = clean.split(/[-_]/);
  const langName = safeDisplayName('language', lang) || lang;
  const regionName = region ? safeDisplayName('region', region.toUpperCase()) : null;
  return regionName ? `${langName} (${regionName})` : langName;
};

/** "LB" -> "Lebanon" */
const describeRegion = (code) => (code ? safeDisplayName('region', String(code).toUpperCase()) || code : null);

/** Human-readable UTC offset from minutes ("-180" -> "UTC+3") */
const describeUtcOffset = (offsetMinutes) => {
  if (typeof offsetMinutes !== 'number') return null;
  const total = -offsetMinutes; // JS getTimezoneOffset is inverted
  const sign = total >= 0 ? '+' : '-';
  const h = Math.floor(Math.abs(total) / 60);
  const m = Math.abs(total) % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
};

// ---------------------------------------------------------------------------
// Device-type helpers
// ---------------------------------------------------------------------------

const DEVICE_TYPE_META = {
  mobile: { icon: DevicePhoneMobileIcon, key: 'phone', fallback: 'Phone' },
  tablet: { icon: DeviceTabletIcon, key: 'tablet', fallback: 'Tablet' },
  smarttv: { icon: TvIcon, key: 'smartTv', fallback: 'Smart TV' },
  desktop: { icon: ComputerDesktopIcon, key: 'computer', fallback: 'Computer' }
};

const normalizeDeviceType = (type) => {
  const tt = (type || '').toLowerCase();
  if (tt === 'mobile' || tt === 'phone') return 'mobile';
  if (tt === 'tablet') return 'tablet';
  if (tt === 'smarttv' || tt === 'tv') return 'smarttv';
  return 'desktop';
};

const IN_APP_BROWSER_COLORS = {
  facebook: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  instagram: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200',
  messenger: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
  whatsapp: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  telegram: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  tiktok: 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900',
  snapchat: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
};

const connectionQuality = (effectiveType) => {
  switch ((effectiveType || '').toLowerCase()) {
    case '4g':
      return { key: 'fast', fallback: 'Fast', tone: 'text-green-600 dark:text-green-400' };
    case '3g':
      return { key: 'moderate', fallback: 'Moderate', tone: 'text-amber-600 dark:text-amber-400' };
    case '2g':
    case 'slow-2g':
      return { key: 'slow', fallback: 'Slow', tone: 'text-red-600 dark:text-red-400' };
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Derive everything a human cares about from the raw attributes
// ---------------------------------------------------------------------------

export const deriveDeviceSummary = (attributes) => {
  const info = attributes?.lastDeviceInfo || null;
  const server = info?.server || null;
  const client = info?.client || null;
  const parsed = server?.userAgentParsed || null;
  const edge = server?.edge || null;
  const knownDeviceIds = normalizeStringSet(attributes?.knownDeviceIds);

  const hasAnyData = !!(
    info ||
    attributes?.lastDeviceSummary ||
    attributes?.lastDeviceId ||
    attributes?.lastUserAgent ||
    knownDeviceIds.length
  );

  const rawUserAgent = attributes?.lastUserAgent || server?.userAgent || client?.navigator?.userAgent || null;
  const maxTouchPoints = client?.navigator?.maxTouchPoints ?? null;
  const uaIsIpad =
    /iPad/.test(rawUserAgent || '') ||
    (/Macintosh/.test(rawUserAgent || '') && (maxTouchPoints || 0) > 1) ||
    String(parsed?.os?.name || '').toLowerCase() === 'ipados';

  const deviceType = normalizeDeviceType(
    (uaIsIpad ? 'tablet' : null) ||
      parsed?.device?.type ||
      (client?.userAgentData?.mobile ? 'mobile' : null) ||
      (edge?.['is-mobile-viewer'] === 'true' ? 'mobile' : null) ||
      (edge?.['is-tablet-viewer'] === 'true' ? 'tablet' : null) ||
      (edge?.['is-smarttv-viewer'] === 'true' ? 'smarttv' : null) ||
      (maxTouchPoints > 1 && (client?.screen?.width || 0) < 900 ? 'mobile' : null) ||
      'desktop'
  );

  const browser = parsed?.browser?.name
    ? `${parsed.browser.name}${parsed.browser.version ? ` ${String(parsed.browser.version).split('.')[0]}` : ''}`
    : null;
  const osName = uaIsIpad && String(parsed?.os?.name || '').toLowerCase() === 'ios' ? 'iPadOS' : parsed?.os?.name || null;
  const os = osName ? `${osName}${parsed?.os?.version ? ` ${parsed.os.version}` : ''}` : null;

  const modelInfo = resolveDeviceModel({
    vendor: parsed?.device?.vendor || null,
    model: parsed?.device?.model || client?.userAgentData?.model || null,
    userAgent: rawUserAgent,
    osName: parsed?.os?.name || null,
    osVersion: parsed?.os?.version || client?.userAgentData?.platformVersion || null,
    screenWidth: client?.screen?.width || null,
    screenHeight: client?.screen?.height || null,
    dpr: client?.viewport?.devicePixelRatio || null,
    maxTouchPoints,
    platform: client?.navigator?.platform || client?.userAgentData?.platform || null,
    deviceType: parsed?.device?.type || null
  });
  const model = modelInfo.name;

  const inApp = server?.inAppBrowser || null;
  const isWebView = !!server?.isWebView;
  const isBot = !!server?.isBot;
  const isAutomation = client?.navigator?.webdriver === true;
  const isNewDevice =
    client?.device?.idIsNew === true || /\(new\)/i.test(attributes?.lastDeviceSummary || info?.summary || '');

  const capturedAt = info?.capturedAt || attributes?.lastLoginTime || null;
  const deviceId = attributes?.lastDeviceId || info?.deviceId || client?.device?.id || null;
  const fingerprint =
    attributes?.lastDeviceFingerprint || info?.deviceFingerprint || client?.device?.fingerprint?.hash || null;
  const ip = [info?.ipAddress, attributes?.lastIPAddress].find((v) => v && v !== 'unknown') || null;
  const siteDomain = info?.siteDomain || client?.page?.hostname || null;

  const screenW = client?.screen?.width;
  const screenH = client?.screen?.height;
  const screen = screenW && screenH ? `${screenW} × ${screenH}` : null;

  const languageTag = client?.navigator?.language || server?.headers?.['accept-language']?.split(',')[0] || null;
  const language = describeLanguage(languageTag);

  const timezone = client?.locale?.timezone || edge?.['viewer-time-zone'] || null;
  const utcOffset = describeUtcOffset(client?.locale?.timezoneOffsetMinutes);

  const connQuality = connectionQuality(client?.network?.effectiveType);
  const connection = client?.network
    ? {
        quality: connQuality,
        effectiveType: client.network.effectiveType ? String(client.network.effectiveType).toUpperCase() : null,
        downlinkMbps: client.network.downlinkMbps ?? null,
        saveData: client.network.saveData === true,
        type: client.network.type || null
      }
    : null;

  const battery =
    client?.battery && typeof client.battery.level === 'number'
      ? {
          percent: Math.round(client.battery.level <= 1 ? client.battery.level * 100 : client.battery.level),
          charging: client.battery.charging === true
        }
      : null;

  const displayMode = client?.preferences?.displayMode || null;
  const installedAsApp = !!((displayMode && displayMode !== 'browser') || client?.preferences?.iosStandalone === true);

  const city = edge?.['viewer-city'] || null;
  const region = edge?.['viewer-country-region-name'] || null;
  const country = edge?.['viewer-country-name'] || describeRegion(edge?.['viewer-country']) || null;
  const location = [city, region && region !== city ? region : null, country].filter(Boolean).join(', ') || null;

  const appVersion = client?.app?.version || null;
  const appEnv = client?.app?.env || null;
  const darkMode = client?.preferences?.prefersColorScheme === 'dark';

  const summaryLine = attributes?.lastDeviceSummary || info?.summary || null;

  const warnings = [];
  if (isBot) warnings.push('bot');
  if (isAutomation) warnings.push('automation');
  if (client?._rejected) warnings.push('rejected');
  if (client?._truncated) warnings.push('truncated');
  if (knownDeviceIds.length > 3) warnings.push('multiDevice');

  return {
    info,
    server,
    client,
    parsed,
    knownDeviceIds,
    hasAnyData,
    deviceType,
    browser,
    os,
    model,
    modelInfo,
    inApp,
    isWebView,
    isBot,
    isAutomation,
    isNewDevice,
    capturedAt,
    deviceId,
    fingerprint,
    ip,
    siteDomain,
    screen,
    language,
    languageTag,
    timezone,
    utcOffset,
    connection,
    battery,
    displayMode,
    installedAsApp,
    location,
    appVersion,
    appEnv,
    darkMode,
    summaryLine,
    warnings
  };
};

// ---------------------------------------------------------------------------
// Small UI atoms
// ---------------------------------------------------------------------------

const CopyButton = ({ value, label, className = '' }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  if (value === null || value === undefined || value === '') return null;

  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        const ok = await copyToClipboard(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
        if (ok) setCopied(true);
      }}
      className={`inline-flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50
        dark:hover:text-blue-300 dark:hover:bg-blue-900/30 transition-colors flex-shrink-0 ${className}`}
      aria-label={label || 'Copy'}
      title={label || 'Copy'}
    >
      {copied ? (
        <ClipboardDocumentCheckIcon className="w-4 h-4 text-green-500" />
      ) : (
        <ClipboardDocumentIcon className="w-4 h-4" />
      )}
    </button>
  );
};

const BoolPill = ({ value }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      value
        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    }`}
  >
    {value ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <XCircleIcon className="w-3.5 h-3.5" />}
    {value ? 'Yes' : 'No'}
  </span>
);

const Chip = ({ children, className = '' }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
      bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 ${className}`}
  >
    {children}
  </span>
);

const EmptyNotice = ({ text }) => (
  <div className="text-center py-8 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
    <DevicePhoneMobileIcon className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
    <p className="text-sm text-gray-500 dark:text-gray-400 px-4">{text}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Technical renderer (pruned: empty values never shown)
// ---------------------------------------------------------------------------

const ValueRenderer = ({ value, keyName = '', depth = 0 }) => {
  if (typeof value === 'boolean') return <BoolPill value={value} />;

  if (typeof value === 'number') {
    if (looksLikeBytesKey(keyName)) {
      return (
        <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">{formatBytes(value)}</span>
      );
    }
    if (looksLikeTimestampKey(keyName) && value > 1e9) {
      return <span className="text-sm font-medium text-gray-900 dark:text-white">{formatDateTime(value)}</span>;
    }
    return (
      <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">
        {Number.isInteger(value) ? value.toLocaleString() : value}
      </span>
    );
  }

  if (typeof value === 'string') {
    if (looksLikeTimestampKey(keyName) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const rel = relativeTime(value);
      return (
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {formatDateTime(value)}
          {rel && <span className="ml-1 text-xs text-gray-400">({rel})</span>}
        </span>
      );
    }
    const mono = looksLikeMonoKey(keyName) || value.length > 48;
    return (
      <span
        className={`text-sm text-gray-900 dark:text-white break-all ${
          mono ? 'font-mono text-xs leading-relaxed' : 'font-medium'
        }`}
      >
        {value}
      </span>
    );
  }

  if (Array.isArray(value)) {
    const allPrimitive = value.every((v) => typeof v !== 'object');
    if (allPrimitive) {
      return (
        <div className="flex flex-wrap gap-1.5 sm:justify-end">
          {value.map((v, i) => (
            <Chip key={`${String(v)}-${i}`} className="font-mono">
              {String(v)}
            </Chip>
          ))}
        </div>
      );
    }
    return (
      <div className="w-full space-y-2">
        {value.map((item, i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40 p-2"
          >
            {isPlainObject(item) ? (
              <KeyValueList data={item} depth={depth + 1} />
            ) : (
              <ValueRenderer value={item} depth={depth + 1} />
            )}
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    return (
      <div className="w-full">
        <KeyValueList data={value} depth={depth + 1} />
      </div>
    );
  }

  return <span className="text-sm text-gray-900 dark:text-white">{String(value)}</span>;
};

const KeyValueList = ({ data, depth = 0, filter = '' }) => {
  const [openKeys, setOpenKeys] = useState({});
  if (!isPlainObject(data)) return <ValueRenderer value={data} depth={depth} />;

  const entries = Object.entries(data).filter(([, v]) => !isEmptyValue(v));
  const q = filter.trim().toLowerCase();

  const matches = (key, value) => {
    if (!q) return true;
    if (key.toLowerCase().includes(q) || humanizeKey(key).toLowerCase().includes(q)) return true;
    try {
      const s = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return s.toLowerCase().includes(q);
    } catch (e) {
      return false;
    }
  };

  const visible = entries.filter(([k, v]) => matches(k, v));
  if (visible.length === 0) return null;

  return (
    <dl className={`divide-y divide-gray-100 dark:divide-gray-700/70 ${depth > 0 ? 'text-sm' : ''}`}>
      {visible.map(([key, value]) => {
        const nested = isPlainObject(value);
        const nestedArray = Array.isArray(value) && value.some((v) => typeof v === 'object');

        if (nested || nestedArray) {
          const isOpen = openKeys[key] ?? (!!q || depth >= 1);
          const count = countLeaves(value);
          return (
            <div key={key} className="py-1.5">
              <button
                type="button"
                onClick={() => setOpenKeys((p) => ({ ...p, [key]: !isOpen }))}
                className="w-full flex items-center justify-between gap-2 py-1 text-left"
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <ChevronRightIcon
                    className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  />
                  {humanizeKey(key)}
                </span>
                <span className="text-xs text-gray-400 tabular-nums">{count}</span>
              </button>
              {isOpen && (
                <div className="mt-1 ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700">
                  <ValueRenderer value={value} keyName={key} depth={depth} />
                </div>
              )}
            </div>
          );
        }

        const copyable = typeof value === 'string' && value.length >= 12;
        return (
          <div
            key={key}
            className="py-2 flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
          >
            <dt className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 sm:w-2/5 flex-shrink-0 sm:pt-0.5">
              {humanizeKey(key)}
            </dt>
            <dd className="min-w-0 sm:flex-1 flex items-start justify-between sm:justify-end gap-1 text-left sm:text-right">
              <div className="min-w-0 flex-1 sm:flex-none">
                <ValueRenderer value={value} keyName={key} depth={depth} />
              </div>
              {copyable && <CopyButton value={value} label={`Copy ${humanizeKey(key)}`} className="-my-1" />}
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

/** Accordion group. Returns null when the (pruned) data is empty. */
const Group = ({ title, icon: Icon, data, color = 'from-gray-500 to-gray-600', defaultOpen = false, filter = '', description }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (filter) setOpen(true);
  }, [filter]);

  const pruned = useMemo(() => pruneEmpty(data), [data]);
  if (isEmptyValue(pruned)) return null;
  const count = countLeaves(pruned);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className={`p-2 rounded-lg bg-gradient-to-br ${color} flex-shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{title}</p>
          {description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{description}</p>}
        </div>
        <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">{count}</span>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-700/70">
          {isPlainObject(pruned) ? (
            <KeyValueList data={pruned} filter={filter} />
          ) : (
            <div className="py-2">
              <ValueRenderer value={pruned} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SERVER_GROUPS = [
  { key: 'userAgentParsed', title: 'Browser, OS & Device (parsed)', icon: CpuChipIcon, color: 'from-blue-500 to-indigo-600' },
  { key: 'clientHints', title: 'Client Hints (sec-ch-*)', icon: SparklesIcon, color: 'from-violet-500 to-purple-600' },
  { key: 'edge', title: 'Edge / CloudFront Viewer', icon: MapPinIcon, color: 'from-emerald-500 to-teal-600' },
  { key: 'headers', title: 'Raw Headers', icon: CodeBracketIcon, color: 'from-gray-500 to-gray-700' },
  { key: 'request', title: 'API Gateway Request', icon: ServerStackIcon, color: 'from-orange-500 to-amber-600' }
];

const CLIENT_GROUPS = [
  { key: 'device', title: 'Device Identity', icon: IdentificationIcon, color: 'from-blue-500 to-indigo-600' },
  { key: 'navigator', title: 'Navigator', icon: GlobeAltIcon, color: 'from-sky-500 to-blue-600' },
  { key: 'userAgentData', title: 'User-Agent Data (Chromium)', icon: SparklesIcon, color: 'from-violet-500 to-purple-600' },
  { key: 'screen', title: 'Screen', icon: ComputerDesktopIcon, color: 'from-fuchsia-500 to-pink-600' },
  { key: 'viewport', title: 'Viewport', icon: WindowIcon, color: 'from-pink-500 to-rose-600' },
  { key: 'locale', title: 'Locale & Time', icon: LanguageIcon, color: 'from-amber-500 to-orange-600' },
  { key: 'network', title: 'Network', icon: WifiIcon, color: 'from-emerald-500 to-green-600' },
  { key: 'graphics', title: 'Graphics / WebGL', icon: CpuChipIcon, color: 'from-teal-500 to-cyan-600' },
  { key: 'preferences', title: 'Preferences & Media Queries', icon: Squares2X2Icon, color: 'from-indigo-500 to-violet-600' },
  { key: 'storage', title: 'Storage', icon: ServerStackIcon, color: 'from-slate-500 to-gray-600' },
  { key: 'battery', title: 'Battery', icon: Battery50Icon, color: 'from-lime-500 to-green-600' },
  { key: 'page', title: 'Page', icon: WindowIcon, color: 'from-cyan-500 to-sky-600' },
  { key: 'performance', title: 'Performance', icon: BoltIcon, color: 'from-yellow-500 to-amber-600' },
  { key: 'features', title: 'Browser Features', icon: CheckCircleIcon, color: 'from-green-500 to-emerald-600' },
  { key: 'app', title: 'Casino App State', icon: SparklesIcon, color: 'from-rose-500 to-red-600' }
];

// ---------------------------------------------------------------------------
// Overview (plain-language) view
// ---------------------------------------------------------------------------

const InfoRow = ({ icon: Icon, label, value, sub, onClick, copy, tone = '' }) => {
  if (isEmptyValue(value)) return null;
  const inner = (
    <>
      <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700/70 flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-sm font-medium text-gray-900 dark:text-white break-words ${tone}`}>{value}</p>
        {sub && <p className="text-xs text-gray-500 dark:text-gray-400 break-words">{sub}</p>}
      </div>
    </>
  );
  return (
    <div className="flex items-center gap-3 py-2.5">
      {onClick ? (
        <button type="button" onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left group">
          {inner}
          <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-500 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </button>
      ) : (
        inner
      )}
      {copy && <CopyButton value={copy} label={`Copy ${label}`} />}
    </div>
  );
};

const Card = ({ title, icon: Icon, children, tone = '' }) => (
  <div className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden ${tone}`}>
    {title && (
      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</h3>
      </div>
    )}
    <div className="px-4 pb-2 divide-y divide-gray-100 dark:divide-gray-700/70">{children}</div>
  </div>
);

const DeviceOverview = ({ attributes, onViewIP, onShowTechnical }) => {
  const { t } = useTranslation();
  const [showDevices, setShowDevices] = useState(false);
  const s = useMemo(() => deriveDeviceSummary(attributes), [attributes]);

  if (!s.hasAnyData) {
    return (
      <EmptyNotice
        text={t('userDeepView.device.noData', 'No device data captured yet. It will be recorded on this user’s next login.')}
      />
    );
  }

  const typeMeta = DEVICE_TYPE_META[s.deviceType] || DEVICE_TYPE_META.desktop;
  const TypeIcon = typeMeta.icon;
  const typeLabel = t(`userDeepView.device.${typeMeta.key}`, typeMeta.fallback);

  // How confident are we about the model name?
  const candidates = s.modelInfo?.candidates || [];
  let modelHint = null;
  if (s.modelInfo?.confidence === 'exact') {
    modelHint = t('userDeepView.device.modelExact', 'Exact model (reported by the in-app browser)');
  } else if (s.modelInfo?.confidence === 'inferred') {
    modelHint =
      candidates.length > 1
        ? t('userDeepView.device.modelInferredMany', { count: candidates.length })
        : t('userDeepView.device.modelInferredOne', 'Identified from screen size and iOS version');
  } else if (s.modelInfo?.confidence === 'code' && s.modelInfo?.code && s.model && !s.model.includes(s.modelInfo.code)) {
    modelHint = `${t('userDeepView.device.modelCode', 'Model code')}: ${s.modelInfo.code}`;
  } else if (s.modelInfo?.confidence === 'unknown' && s.modelInfo?.vendor === 'Apple') {
    modelHint = t('userDeepView.device.modelAppleUnknown', 'Apple does not expose the exact model to websites');
  }

  // "Opened from" sentence
  let openedFrom = null;
  let openedFromTone = '';
  if (s.inApp) {
    openedFrom = t('userDeepView.device.openedInApp', { app: s.inApp });
    openedFromTone = 'text-amber-700 dark:text-amber-300';
  } else if (s.installedAsApp) {
    openedFrom = t('userDeepView.device.openedInstalled', 'Installed app on home screen');
    openedFromTone = 'text-emerald-700 dark:text-emerald-300';
  } else if (s.isWebView) {
    openedFrom = t('userDeepView.device.openedWebView', 'Embedded browser (WebView)');
  } else if (s.browser) {
    openedFrom = t('userDeepView.device.openedBrowser', 'Regular browser');
  }

  const connectionValue = s.connection
    ? [
        s.connection.quality
          ? t(`userDeepView.device.${s.connection.quality.key}`, s.connection.quality.fallback)
          : null,
        s.connection.effectiveType ? `(${s.connection.effectiveType})` : null
      ]
        .filter(Boolean)
        .join(' ') || s.connection.type
    : null;
  const connectionSub = s.connection
    ? [
        s.connection.downlinkMbps ? `≈ ${s.connection.downlinkMbps} Mbps` : null,
        s.connection.saveData ? t('userDeepView.device.saveData', 'Data saver on') : null
      ]
        .filter(Boolean)
        .join(' · ') || null
    : null;

  const batteryValue = s.battery
    ? `${s.battery.percent}%${s.battery.charging ? ` · ${t('userDeepView.device.charging', 'charging')}` : ''}`
    : null;
  const batteryTone = s.battery && !s.battery.charging && s.battery.percent <= 15 ? 'text-red-600 dark:text-red-400' : '';

  const deviceCount = s.knownDeviceIds.length;
  const visualNames = (candidates.length
    ? candidates
    : s.model
      ? [s.model]
      : []
  ).slice(0, 6);
  const heroVisuals = visualNames.slice(0, 3);

  const warningText = {
    bot: t('userDeepView.device.warnBot', 'This login looked like an automated bot, not a real person.'),
    automation: t('userDeepView.device.warnAutomation', 'The browser reported it is being controlled by automation software.'),
    rejected: t('userDeepView.device.warnRejected', 'The device report sent by the browser was malformed and was rejected.'),
    truncated: t('userDeepView.device.warnTruncated', 'The device report was very large and was shortened.'),
    multiDevice: t('userDeepView.device.warnMultiDevice', 'This account has been used on more than 3 different devices.')
  };

  return (
    <div className="space-y-3">
      {/* Hero */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-600 text-white shadow-md">
        {heroVisuals.length > 0 && (
          <div className="flex justify-center gap-2.5 mb-4 flex-wrap">
            {heroVisuals.map((n) => (
              <div
                key={n}
                className="rounded-2xl bg-black/20 backdrop-blur-sm px-3 pt-3 pb-2 ring-1 ring-white/20"
              >
                <DeviceMockup name={n} size={heroVisuals.length > 2 ? 108 : 132} variant="hero" />
              </div>
            ))}
          </div>
        )}
        <div className="flex items-start gap-4">
          {heroVisuals.length === 0 && (
            <div className="p-3 rounded-2xl bg-white/20 backdrop-blur flex-shrink-0">
              <TypeIcon className="w-9 h-9" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-white/70">{typeLabel}</p>
            <p className="text-xl font-bold leading-tight break-words">
              {s.model || s.os || t('userDeepView.device.unknownDevice', 'Unknown device')}
            </p>
            {(s.os || s.browser) && (
              <p className="text-sm text-white/85 mt-0.5">{[s.os, s.browser].filter(Boolean).join(' · ')}</p>
            )}
            {modelHint && <p className="text-[11px] text-white/70 mt-1 leading-snug">{modelHint}</p>}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {s.isNewDevice ? (
            <Chip className="bg-amber-300/90 text-amber-950">
              <SparklesIcon className="w-3.5 h-3.5 mr-1" />
              {t('userDeepView.device.newDevice', 'First time on this device')}
            </Chip>
          ) : deviceCount > 0 ? (
            <Chip className="bg-white/20 text-white">
              <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
              {t('userDeepView.device.knownDevice', 'Known device')}
            </Chip>
          ) : null}
          {s.inApp && (
            <Chip className={IN_APP_BROWSER_COLORS[String(s.inApp).toLowerCase()] || 'bg-white/20 text-white'}>
              {s.inApp}
            </Chip>
          )}
          {s.installedAsApp && !s.inApp && (
            <Chip className="bg-emerald-300/90 text-emerald-950">
              <ArrowDownTrayIcon className="w-3.5 h-3.5 mr-1" />
              {t('userDeepView.device.installedApp', 'Installed as app')}
            </Chip>
          )}
          {s.darkMode && <Chip className="bg-white/20 text-white">{t('userDeepView.device.darkMode', 'Dark mode')}</Chip>}
          {s.isBot && (
            <Chip className="bg-red-500 text-white">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" /> Bot
            </Chip>
          )}
        </div>
      </div>

      {/* Warnings */}
      {s.warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-1.5">
          {s.warnings.map((w) => (
            <p key={w} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
              <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {warningText[w]}
            </p>
          ))}
        </div>
      )}

      {/* Login */}
      <Card title={t('userDeepView.device.sectionLogin', 'Last login')} icon={ClockIcon}>
        <InfoRow
          icon={ClockIcon}
          label={t('userDeepView.device.lastSeen', 'When')}
          value={s.capturedAt ? formatDateTime(s.capturedAt) : null}
          sub={relativeTime(s.capturedAt)}
        />
        <InfoRow icon={MapPinIcon} label={t('userDeepView.device.location', 'Where')} value={s.location} />
        <InfoRow
          icon={GlobeAltIcon}
          label={t('userDeepView.device.ipAddress', 'IP address')}
          value={s.ip}
          sub={onViewIP ? t('userDeepView.device.lookupIp', 'Tap to look up location & risk') : null}
          onClick={onViewIP && s.ip ? () => onViewIP(s.ip) : undefined}
          copy={s.ip}
        />
        <InfoRow icon={WindowIcon} label={t('userDeepView.device.site', 'Website')} value={s.siteDomain} />
        <InfoRow
          icon={ArrowDownTrayIcon}
          label={t('userDeepView.device.openedFrom', 'Opened from')}
          value={openedFrom}
          tone={openedFromTone}
        />
      </Card>

      {/* Device */}
      <Card title={t('userDeepView.device.sectionDevice', 'Device')} icon={TypeIcon}>
        <InfoRow icon={TypeIcon} label={t('userDeepView.device.type', 'Type')} value={typeLabel} />
        <InfoRow
          icon={IdentificationIcon}
          label={t('userDeepView.device.model', 'Model')}
          value={s.model}
          sub={modelHint}
        />
        {candidates.length > 1 && (
          <div className="py-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              {t('userDeepView.device.possibleModels', 'Possible models')}
            </p>
            <div className={`grid gap-2 ${candidates.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
              {candidates.map((name) => (
                <div
                  key={name}
                  className="rounded-2xl bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 px-2 pt-3 pb-2.5 flex justify-center"
                >
                  <DeviceMockup name={name} size={120} />
                </div>
              ))}
            </div>
          </div>
        )}
        <InfoRow icon={CpuChipIcon} label={t('userDeepView.device.system', 'System')} value={s.os} />
        <InfoRow icon={GlobeAltIcon} label={t('userDeepView.device.browser', 'Browser')} value={s.browser} />
        <InfoRow icon={ComputerDesktopIcon} label={t('userDeepView.device.screen', 'Screen')} value={s.screen} />
        <InfoRow
          icon={s.battery?.charging ? BoltIcon : Battery50Icon}
          label={t('userDeepView.device.battery', 'Battery')}
          value={batteryValue}
          tone={batteryTone}
        />
        <InfoRow
          icon={WifiIcon}
          label={t('userDeepView.device.connection', 'Connection')}
          value={connectionValue}
          sub={connectionSub}
          tone={s.connection?.quality?.tone || ''}
        />
        <InfoRow icon={LanguageIcon} label={t('userDeepView.device.language', 'Language')} value={s.language} />
        <InfoRow
          icon={ClockIcon}
          label={t('userDeepView.device.timezone', 'Time zone')}
          value={s.timezone}
          sub={s.utcOffset}
        />
        <InfoRow
          icon={SparklesIcon}
          label={t('userDeepView.device.appVersion', 'Casino app version')}
          value={s.appVersion}
          sub={s.appEnv}
        />
      </Card>

      {/* Devices used */}
      {deviceCount > 0 && (
        <Card title={t('userDeepView.device.sectionDevices', 'Devices used')} icon={DevicePhoneMobileIcon}>
          <button
            type="button"
            onClick={() => setShowDevices((v) => !v)}
            className="w-full flex items-center gap-3 py-2.5 text-left"
          >
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700/70 flex-shrink-0">
              <DevicePhoneMobileIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {deviceCount === 1
                  ? t('userDeepView.device.singleDevice', 'Always the same device')
                  : t('userDeepView.device.devicesUsed', { count: deviceCount })}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('userDeepView.device.devicesHint', 'Every device this account has logged in from')}
              </p>
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showDevices ? 'rotate-180' : ''}`}
            />
          </button>
          {showDevices && (
            <ul className="py-2 space-y-1">
              {s.knownDeviceIds.map((id, idx) => {
                const current = id === s.deviceId;
                return (
                  <li
                    key={id}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs ${
                      current
                        ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200'
                        : 'bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span className="w-5 text-gray-400 tabular-nums">#{idx + 1}</span>
                    <span className="flex-1 font-mono truncate" title={id}>
                      {shortId(id, 14, 6)}
                    </span>
                    {current && (
                      <span className="text-[10px] uppercase font-semibold">
                        {t('userDeepView.device.current', 'this login')}
                      </span>
                    )}
                    <CopyButton value={id} label="Copy device ID" className="-my-1" />
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}

      {/* Technical link */}
      {(s.server || s.client) && onShowTechnical && (
        <button
          type="button"
          onClick={onShowTechnical}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <CodeBracketIcon className="w-4 h-4 text-gray-400" />
            {t('userDeepView.device.technicalLink', 'Technical details for support')}
          </span>
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Technical view
// ---------------------------------------------------------------------------

const SectionLabel = ({ children }) => (
  <p className="px-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
    {children}
  </p>
);

const TechnicalView = ({ attributes, filter }) => {
  const { t } = useTranslation();
  const info = attributes?.lastDeviceInfo || {};
  const server = info.server || null;
  const client = info.client || null;

  const identity = {
    lastDeviceId: attributes?.lastDeviceId,
    lastDeviceFingerprint: attributes?.lastDeviceFingerprint,
    lastDeviceSummary: attributes?.lastDeviceSummary,
    lastUserAgent: attributes?.lastUserAgent,
    lastIPAddress: attributes?.lastIPAddress,
    lastLoginTime: attributes?.lastLoginTime,
    updatedAt: attributes?.updatedAt,
    knownDeviceIds: normalizeStringSet(attributes?.knownDeviceIds),
    capturedAt: info.capturedAt,
    siteDomain: info.siteDomain
  };

  const serverFlags = server
    ? { isWebView: server.isWebView, inAppBrowser: server.inAppBrowser, isBot: server.isBot, userAgent: server.userAgent }
    : null;

  const clientMeta = client
    ? Object.fromEntries(Object.entries(client).filter(([k]) => !CLIENT_GROUPS.some((g) => g.key === k)))
    : null;

  if (!server && !client && isEmptyValue(pruneEmpty(identity))) {
    return <EmptyNotice text={t('userDeepView.device.noTechnical', 'No technical data recorded for this user yet.')} />;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
        {t('userDeepView.device.hiddenEmptyNote', 'Empty or unsupported fields are hidden automatically.')}
      </p>

      <Group
        title={t('userDeepView.device.identityGroup', 'Identity & Capture')}
        icon={IdentificationIcon}
        color="from-violet-500 to-indigo-600"
        data={identity}
        defaultOpen
        filter={filter}
      />

      {server && (
        <>
          <SectionLabel>{t('userDeepView.device.serverSection', 'Seen by the server')}</SectionLabel>
          <Group
            title={t('userDeepView.device.flagsGroup', 'Environment Flags')}
            icon={ShieldExclamationIcon}
            color="from-amber-500 to-orange-600"
            data={serverFlags}
            filter={filter}
          />
          {SERVER_GROUPS.map((g) => (
            <Group key={g.key} title={g.title} icon={g.icon} color={g.color} data={server[g.key]} filter={filter} />
          ))}
          {Object.keys(server)
            .filter((k) => !SERVER_GROUPS.some((g) => g.key === k) && !(k in (serverFlags || {})))
            .map((k) => (
              <Group key={k} title={humanizeKey(k)} icon={CodeBracketIcon} data={server[k]} filter={filter} />
            ))}
        </>
      )}

      {client && (
        <>
          <SectionLabel>{t('userDeepView.device.clientSection', 'Reported by the browser')}</SectionLabel>
          <Group
            title={t('userDeepView.device.clientMetaGroup', 'Report Meta')}
            icon={CodeBracketIcon}
            color="from-gray-500 to-gray-700"
            data={clientMeta}
            filter={filter}
          />
          {CLIENT_GROUPS.map((g) => (
            <Group key={g.key} title={g.title} icon={g.icon} color={g.color} data={client[g.key]} filter={filter} />
          ))}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bottom sheet / modal
// ---------------------------------------------------------------------------

const DeviceDetailsSheet = ({ attributes, onClose, isIOS, onViewIP, allowTechnical = true }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const rawJson = useMemo(() => {
    const payload = pruneEmpty({
      lastDeviceId: attributes?.lastDeviceId,
      knownDeviceIds: normalizeStringSet(attributes?.knownDeviceIds),
      lastDeviceFingerprint: attributes?.lastDeviceFingerprint,
      lastUserAgent: attributes?.lastUserAgent,
      lastDeviceSummary: attributes?.lastDeviceSummary,
      lastIPAddress: attributes?.lastIPAddress,
      lastLoginTime: attributes?.lastLoginTime,
      updatedAt: attributes?.updatedAt,
      lastDeviceInfo: attributes?.lastDeviceInfo
    });
    try {
      return JSON.stringify(payload, null, 2);
    } catch (e) {
      return '{}';
    }
  }, [attributes]);

  const tabs = [
    { key: 'overview', label: t('userDeepView.device.tabOverview', 'Overview') },
    ...(allowTechnical
      ? [
          { key: 'technical', label: t('userDeepView.device.tabTechnical', 'Technical') },
          { key: 'raw', label: 'JSON' }
        ]
      : [])
  ];

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4 z-50 animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-gray-50 dark:bg-gray-900 w-full md:max-w-2xl h-[92vh] md:h-auto md:max-h-[90vh] flex flex-col shadow-2xl
          ${isIOS ? 'rounded-t-3xl md:rounded-3xl' : 'rounded-t-2xl md:rounded-2xl'}
          ${isMobile ? 'animate-slideUp' : 'animate-scaleIn'}`}
      >
        {/* Header */}
        <div
          className={`flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 pt-3 pb-3
            ${isIOS ? 'rounded-t-3xl' : 'rounded-t-2xl'}`}
        >
          <div className="md:hidden mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 min-w-0">
              <div className="p-2 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex-shrink-0">
                <FingerPrintIcon className="w-5 h-5 text-white" />
              </div>
              <span className="truncate">{t('userDeepView.device.detailsTitle', 'Device & Login Details')}</span>
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
              aria-label={t('common.close', 'Close')}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {tabs.length > 1 && (
          <div className={`mt-3 grid gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-700/60 ${tabs.length === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {tabs.map((tb) => (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                className={`px-2 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all truncate
                  ${tab === tb.key
                    ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
              >
                {tb.label}
              </button>
            ))}
          </div>
          )}

          {tab === 'technical' && (
            <div className="mt-3 relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('userDeepView.device.searchPlaceholder', 'Search fields…')}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {tab === 'overview' && (
            <DeviceOverview
              attributes={attributes}
              onShowTechnical={allowTechnical ? () => setTab('technical') : undefined}
              onViewIP={
                onViewIP
                  ? (ip) => {
                      onClose();
                      onViewIP(ip);
                    }
                  : undefined
              }
            />
          )}

          {tab === 'technical' && <TechnicalView attributes={attributes} filter={filter} />}

          {tab === 'raw' && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-900 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
                <span className="text-xs font-mono text-gray-300">UserAttributes · device fields</span>
                <CopyButton value={rawJson} label="Copy JSON" className="text-gray-300 hover:bg-gray-700" />
              </div>
              <pre className="p-3 text-[11px] leading-relaxed text-green-200 overflow-x-auto whitespace-pre max-h-[60vh]">
                {rawJson}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3
            ${isIOS ? 'pb-safe' : ''}`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700
              text-white font-medium transition-all active:scale-[0.98] shadow-lg ${isIOS ? 'rounded-2xl' : 'rounded-xl'}`}
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Compact inline row on the deep view page -> opens the sheet
// ---------------------------------------------------------------------------

const DeviceInfoPanel = ({ attributes, isIOS = false, onViewIP, allowTechnical = true }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const s = useMemo(() => deriveDeviceSummary(attributes), [attributes]);

  const typeMeta = DEVICE_TYPE_META[s.deviceType] || DEVICE_TYPE_META.desktop;
  const TypeIcon = typeMeta.icon;
  const typeLabel = t(`userDeepView.device.${typeMeta.key}`, typeMeta.fallback);

  const headline = s.hasAnyData
    ? [s.model ? `${typeLabel} · ${s.model}` : typeLabel, s.os].filter(Boolean).join(' · ')
    : t('userDeepView.device.noDataShort', 'No device data yet');

  const rel = s.capturedAt ? relativeTime(s.capturedAt) : null;
  const subline = s.hasAnyData
    ? [rel ? `${t('userDeepView.device.lastSeenShort', 'Last seen')} ${rel}` : null, s.location, s.inApp ? `${t('userDeepView.device.via', 'via')} ${s.inApp}` : null]
        .filter(Boolean)
        .join(' · ')
    : null;

  return (
    <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm ${isIOS ? 'rounded-2xl' : ''}`}>
      <button
        type="button"
        onClick={() => s.hasAnyData && setShowDetails(true)}
        disabled={!s.hasAnyData}
        className={`w-full flex items-center gap-3 p-4 text-left rounded-lg transition-colors
          ${isIOS ? 'rounded-2xl' : ''}
          ${s.hasAnyData ? 'hover:bg-gray-50 dark:hover:bg-gray-700/40 active:scale-[0.99]' : 'cursor-default'}`}
        aria-label={t('userDeepView.device.viewFullDetails', 'View device details')}
      >
        <div
          className={`p-2.5 rounded-xl flex-shrink-0 ${
            s.hasAnyData ? 'bg-gradient-to-br from-violet-500 to-indigo-600 shadow-sm' : 'bg-gray-100 dark:bg-gray-700'
          }`}
        >
          {s.hasAnyData ? (
            <TypeIcon className="w-5 h-5 text-white" />
          ) : (
            <FingerPrintIcon className="w-5 h-5 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {t('userDeepView.device.title', 'Device & Login')}
            </h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{headline}</p>
          {subline && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subline}</p>}
        </div>

        {s.hasAnyData && (
          <div className="flex items-center gap-1.5 flex-shrink-0 text-violet-600 dark:text-violet-300">
            {s.isNewDevice && (
              <span className="hidden sm:inline-flex px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 text-[10px] font-semibold">
                {t('userDeepView.device.newShort', 'New')}
              </span>
            )}
            {s.warnings.length > 0 && (
              <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
                {s.warnings.length}
              </span>
            )}
            <ChevronRightIcon className="w-5 h-5" />
          </div>
        )}
      </button>

      {showDetails && (
        <DeviceDetailsSheet
          attributes={attributes}
          isIOS={isIOS}
          onViewIP={onViewIP}
          allowTechnical={allowTechnical}
          onClose={() => setShowDetails(false)}
        />
      )}
    </section>
  );
};

export default DeviceInfoPanel;
